import styled from "@emotion/styled";
import {Box, Modal} from "@mui/material";
import {media} from "assets/css/media";
import {ReactComponent as IconClose} from "assets/img/icon/close.svg";
import {ReactComponent as FacebookIcon} from "assets/img/icon/facebook.svg";
import {ReactComponent as TwitterIcon} from "assets/img/icon/twitter.svg";
import {Bindings} from "data/constants/bindings";
import {useViewController} from "data/services/locator/locator_hook.service";
import {observer} from "mobx-react";
import React from "react";
import {useNavigate} from "react-router-dom";
import {Exist} from "views/components/exist/exist.component";
import {
	CloseBtn,
	ModalBody,
	ModalBox,
	ModalContent,
	ModalFooter,
	ModalTextWOMargin,
	ModalTitle,
} from "views/components/modals/common/common.component";
import {Preloader} from "views/components/preloader";
import {DownloadButton, PrimaryButton, SocialShareButton} from "views/components/style";
import type {IShareController} from "views/controllers/share/share.controller";

const ModalBodyStyled = styled(ModalBody)`
	margin-top: 30px;
`;

const ShareImageBox = styled(Box)`
	width: 100%;

	img {
		margin: 0 auto;
		display: block;
		max-width: 100%;
		border-radius: 10px;
	}
`;

const ButtonRow = styled(Box)`
	display: flex;
	justify-content: center;
	align-items: center;
	width: 100%;
	gap: 12px;

	button {
		flex: unset;
		width: 44px;
		padding: 0;
	}

	@media (max-width: ${media.mobile}) {
		flex-direction: column;
	}
`;

const ButtonMobileRow = styled.div`
	display: flex;
	flex-direction: row;
	gap: 12px;
	width: 100%;

	@media (max-width: ${media.mobile}) {
		flex-direction: column;
	}
`;

const DownloadButtonDesktop = styled(DownloadButton)`
	flex-grow: 0 !important;
`;

export const ModalShare: React.FC = observer(() => {
	const navigate = useNavigate();
	const controller = useViewController<IShareController>(Bindings.ShareController, {navigate});
	const {
		i18n,
		isOpenShareModal,
		closeShareModal,
		shareImagePath,
		isMobileShareVisible,
		shareViaFB,
		shareViaTW,
		shareViaMobile,
	} = controller;

	if (!isOpenShareModal) {
		return null;
	}

	const title = i18n.t("pinpoint.modal.share.title", "Your picks are saved");
	const message = i18n.t(
		"pinpoint.modal.share.description",
		"Your predictions are in! You are in the draw to win a Ryder Cup Store Voucher and Signed Pin Flag."
	);
	const download = isMobileShareVisible
		? i18n.t("pinpoint.modal.share.mobile_download", "Save to photos")
		: i18n.t("pinpoint.modal.share.download", "Download");

	return (
		<Modal open={isOpenShareModal}>
			<ModalBox className="share_box">
				<ModalContent className="share_content">
					<CloseBtn onClick={closeShareModal}>
						<IconClose />
					</CloseBtn>
					<ModalBodyStyled>
						<ShareImageBox>
							{shareImagePath.preview ? (
								<img src={shareImagePath.preview} alt="share" />
							) : (
								<Preloader />
							)}
						</ShareImageBox>
						<ModalTitle>{title}</ModalTitle>
						<ModalTextWOMargin>{message}</ModalTextWOMargin>
					</ModalBodyStyled>
					<ModalFooter>
						<Exist when={!isMobileShareVisible}>
							<ButtonRow>
								<DownloadButtonDesktop
									className={!shareImagePath.link ? "disabled" : ""}
									href={shareImagePath.link}
									download>
									{download}
								</DownloadButtonDesktop>
								<SocialShareButton
									variant="contained"
									onClick={shareViaFB}
									disabled={!shareImagePath.link}>
									<FacebookIcon />
								</SocialShareButton>
								<SocialShareButton
									variant="contained"
									onClick={shareViaTW}
									disabled={!shareImagePath.link}>
									<TwitterIcon />
								</SocialShareButton>
							</ButtonRow>
						</Exist>
						<Exist when={isMobileShareVisible}>
							<ButtonMobileRow>
								<DownloadButton
									className={!shareImagePath.link ? "disabled" : ""}
									href={shareImagePath.link}
									download>
									{download}
								</DownloadButton>
								<PrimaryButton
									disabled={!shareImagePath.link}
									fullWidth
									onClick={shareViaMobile}
									variant="contained"
									size="large">
									{i18n.t("pinpoint.modal.share.share_btn", "Share")}
								</PrimaryButton>
							</ButtonMobileRow>
						</Exist>
					</ModalFooter>
				</ModalContent>
			</ModalBox>
		</Modal>
	);
});
